import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { friendlyAuthError } from '../utils/authErrors';
import '../styles/auth.css';

export default function Verify() {
  const { verify, resendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Email passed from signup page
  const passedEmail = (location.state as { email?: string })?.email || '';

  const [email, setEmail] = useState(passedEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verify(email, code);
      navigate('/login', { state: { verified: true } });
    } catch (err: unknown) {
      setError(friendlyAuthError(err, 'Verification failed — please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Please enter your email first');
      return;
    }
    setResending(true);
    try {
      await resendCode(email);
      setError('');
    } catch (err: unknown) {
      setError(friendlyAuthError(err, "Couldn't resend the code — please try again."));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">Verify your email</h1>
          <p className="auth-subtitle">
            We sent a verification code to your email address.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="auth-error">{error}</div>}

          <div className="form-field">
            <label className="field-label">Email</label>
            <input
              type="email"
              className="field-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-field">
            <label className="field-label">Verification Code</label>
            <input
              type="text"
              className="field-input field-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              required
              autoFocus={!!passedEmail}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Verifying…' : 'Verify email'}
          </button>
        </form>

        <div className="auth-footer">
          <button
            type="button"
            className="btn-text"
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? 'Sending…' : 'Resend code'}
          </button>
          <span className="auth-separator">·</span>
          <Link to="/login" className="auth-link">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
